<template>
	<div>
		<div class="login-logo">
			<img :src="logo" alt="Mini ERP">
		</div>
		<div class="login-main">
			<div class="login-main-header">
				<template v-if="view === 'login'">
					<h3 class="animate__animated animate__fast animate__fadeInDown" style="animation-delay:0.1s">{{ frappe._('Welcome Back') }} 👋</h3>
					<p class="animate__animated animate__fast animate__fadeInUp" style="animation-delay:0.2s">{{ frappe._('Sign in to your account and start your work.') }}</p>
				</template>
				<template v-if="view === 'forgot'">
					<h3 class="animate__animated animate__fast animate__fadeInDown" style="animation-delay:0.1s">{{ frappe._('Forgot Password?') }} 🔒</h3>
					<p class="animate__animated animate__fast animate__fadeInUp" style="animation-delay:0.2s">{{ frappe._('Enter your email address and we will send you a link to reset your password.') }}</p>
				</template>
				<template v-if="view === 'signup' && !minierp.disable_signup">
					<h3 class="animate__animated animate__fast animate__fadeInDown" style="animation-delay:0.1s">{{ frappe._('Create Your Account') }} 🚀</h3>
					<p class="animate__animated animate__fast animate__fadeInUp" style="animation-delay:0.2s">{{ frappe._('Enter your personal details to create account.') }}</p>
				</template>
				<template v-if="view === 'login-link'">
					<h3 class="animate__animated animate__fast animate__fadeInDown" style="animation-delay:0.1s">{{ frappe._('Login with Email Link') }} 📩</h3>
					<p class="animate__animated animate__fast animate__fadeInUp" style="animation-delay:0.2s">{{ frappe._('Enter your email address and we will send you a link to login.') }}</p>
				</template>
			</div>
			<div class="login-main-form">
				<div v-if="error_message" class="alert alert-danger p-2 pr-3 pl-3 font-w-600 animate__animated animate__fast animate__shakeX d-flex align-items-center" style="gap:6px;">
					<svg xmlns="http://www.w3.org/2000/svg" style="height:16px;" viewBox="0 0 512 512">
						<path fill="currentColor" d="M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM216 336c-13.3 0-24 10.7-24 24s10.7 24 24 24l80 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-8 0 0-88c0-13.3-10.7-24-24-24l-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l24 0 0 64-24 0zm40-144a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
					</svg>
					{{ error_message }}
				</div>
				<div v-if="success_message" class="alert alert-success p-2 pr-3 pl-3 font-w-600 animate__animated animate__fast animate__zoomIn d-flex align-items-center" style="gap:6px;">
					<svg xmlns="http://www.w3.org/2000/svg" style="height:16px;" viewBox="0 0 512 512">
						<path fill="currentColor" d="M256 512a256 256 0 1 1 0-512 256 256 0 1 1 0 512zm0-464a208 208 0 1 0 0 416 208 208 0 1 0 0-416zm70.7 121.9c7.8-10.7 22.8-13.1 33.5-5.3 10.7 7.8 13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8-7 .5-13.9-2-18.8-6.9l-55.9-55.9c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l36 36 105.6-145.2z"/>
					</svg>
					{{ success_message }}
				</div>
				<form @submit.prevent="login" v-if="view === 'login'" class="animate__animated animate__faster animate__zoomIn" style="animation-delay:0.2s">
					<div class="form-group">
						<label for="login_username">{{ frappe._(minierp.login_label) }}</label>
						<input type="text" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.login_username}" id="login_username" v-model="login_username" :placeholder="minierp.login_name_placeholder" autocomplete="username">
						<div class="invalid-feedback mt-2" v-if="errors.login_username">{{ errors.login_username }}</div>
					</div>
					<div class="form-group password-field">
						<label for="login_password">{{ frappe._('Password') }}</label>
						<input :type="show_password ? 'text' : 'password'" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.login_password}" id="login_password" v-model="login_password" placeholder="•••••" autocomplete="current-password">
						<div class="invalid-feedback mt-2" v-if="errors.login_password">{{ errors.login_password }}</div>
						<div class="toggle-password" @click="show_password = !show_password">
							<svg xmlns="http://www.w3.org/2000/svg" v-if="!show_password" viewBox="0 0 576 512">
								<path fill="currentColor"
								      d="M288 64c-140.8 0-229.3 128-256 192 26.7 64 115.2 192 256 192 140.8 0 229.3-128 256-192-26.7-64-115.2-192-256-192zM95.4 112.6C142.5 68.8 207.2 32 288 32s145.5 36.8 192.6 80.6c46.8 43.5 78.1 95.4 93 131.1 3.3 7.9 3.3 16.7 0 24.6-14.9 35.7-46.2 87.7-93 131.1-47.1 43.7-111.8 80.6-192.6 80.6S142.5 443.2 95.4 399.4c-46.8-43.5-78.1-95.4-93-131.1-3.3-7.9-3.3-16.7 0-24.6 14.9-35.7 46.2-87.7 93-131.1zM288 352c53 0 96-43 96-96 0-43.3-28.7-79.9-68.1-91.9 2.7 8.8 4.1 18.2 4.1 27.9 0 53-43 96-96 96-9.7 0-19.1-1.4-27.9-4.1 11.9 39.4 48.6 68.1 91.9 68.1zM160.2 263.8c-.2-2.6-.2-5.2-.2-7.8 0-12.2 1.7-23.9 4.9-35 .3-.9 .5-1.8 .8-2.7 12.4-40.4 44.3-72.2 84.7-84.7 11.9-3.7 24.6-5.6 37.7-5.6 2.5 0 5 .1 7.4 .2l.4 0c67.1 4 120.2 59.7 120.2 127.8 0 70.7-57.3 128-128 128-68.1 0-123.8-53.2-127.8-120.2zm32.1-16.1c9.3 5.3 20.1 8.4 31.6 8.4 35.3 0 64-28.7 64-64 0-11.5-3-22.3-8.4-31.6-46.4 4-83.3 40.9-87.3 87.3z"/>
							</svg>
							<svg xmlns="http://www.w3.org/2000/svg" v-if="show_password" viewBox="0 0 576 512">
								<path fill="currentColor"
								      d="M27.3-27.2c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l544 544c6.2 6.2 16.4 6.2 22.6 0s6.2-16.4 0-22.6L466.4 411.9c4.9-4.1 9.6-8.2 14.1-12.5 46.8-43.5 78.1-95.4 93-131.1 3.3-7.9 3.3-16.7 0-24.6-14.9-35.7-46.2-87.7-93-131.1-47.1-43.7-111.8-80.6-192.6-80.6-60 0-111.1 20.3-152.8 48.7L27.3-27.2zm131 131c36.4-23.7 79.7-39.8 129.7-39.8 140.8 0 229.3 128 256 192-13.3 32-42.1 80-85.2 120-4.9 4.5-9.9 8.9-15.1 13.2l-54.6-54.6c16.9-21.7 26.9-48.9 26.9-78.5 0-70.7-57.3-128-128-128-29.6 0-56.9 10-78.5 26.9l-51.2-51.2zM366.2 311.7L232.3 177.8c15.7-11.2 34.9-17.8 55.7-17.8 53 0 96 43 96 96 0 20.8-6.6 40-17.8 55.7zM74.8 133.3c-35.5 38.5-59.7 80.2-72.3 110.4-3.3 7.9-3.3 16.7 0 24.6 14.9 35.7 46.2 87.7 93 131.1 47.1 43.7 111.8 80.6 192.6 80.6 40.6 0 77.1-9.3 109.4-24.1L373 431.5c-25.8 10.3-54.1 16.5-85 16.5-140.8 0-229.3-128-256-192 11.2-26.8 33.2-64.9 65.4-100.1L74.8 133.3z"/>
							</svg>
						</div>
					</div>
					<div class="form-group text-right">
						<a class="cursor-pointer" @click="change_view('forgot')" :class="{'cursor-not-allowed': loading}">{{ frappe._('Forgot Password?') }}</a>
					</div>
					<div class="form-group" v-if="!minierp.disable_user_pass_login">
						<button type="submit" class="btn btn-primary btn-block waves-effect waves-light" :disabled="loading" :class="{ 'loading': loading }">
							<span v-if="!loading">{{ frappe._('Login') }}</span>
						</button>
					</div>
					<div class="form-group login-with" v-if="minierp.login_with_email_link || minierp.social_login">
						<div class="login-with-login-title"><span>{{ frappe._('Or') }}</span></div>
						<div class="login-with-login-buttons">
							<div class="d-flex justify-content-center align-items-end mb-3" v-if="minierp.provider_logins?.length" style="gap: 10px;">
								<template v-for="provider in minierp.provider_logins">
									<button type="button" @click="login_with_provider(provider)" :disabled="loading" :tooltip="frappe._('Login with {0}', [provider.provider_name])" class="btn btn-default btn-icon btn-block">
										<span v-html="provider.icon || provider.provider_name"></span>
									</button>
								</template>
							</div>
							<button type="button" @click="change_view('login-link')" :disabled="loading" v-if="minierp.login_with_email_link" class="btn btn-default btn-block waves-effect waves-gray">{{ frappe._('Login with Email Link') }}</button>
						</div>
					</div>
					<div class="text-center sign-up-message" v-if="!minierp.disable_signup">
						{{ frappe._("Don't have an account?") }}
						<a @click="change_view('signup')" class="cursor-pointer text-primary" :class="{'cursor-not-allowed': loading}" style="font-weight: 600">{{ frappe._("Sign up") }}</a>
					</div>
				</form>
				<form v-if="view === 'forgot'" @submit.prevent="forgot_password" class="animate__animated animate__faster animate__zoomIn">
					<div class="form-group">
						<label for="forgot_username">{{ frappe._('Email Address') }}</label>
						<input type="email" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.forgot_username}" id="forgot_username" v-model="forgot_username" :placeholder="minierp.login_name_placeholder" autocomplete="username">
						<div class="invalid-feedback mt-2" v-if="errors.forgot_username">{{ errors.forgot_username }}</div>
					</div>
					<div class="form-group">
						<button type="submit" class="btn btn-primary btn-block waves-effect waves-light" :disabled="loading" :class="{ 'loading': loading }">
							<span v-if="!loading">{{ frappe._('Reset Password') }}</span>
						</button>
					</div>
					<div class="form-group">
						<button type="button" @click="change_view('login')" :disabled="loading" class="btn btn-link text-primary btn-block waves-effect waves-gray box-shadow-none border-none">{{ frappe._('Back to Login') }}</button>
					</div>
				</form>
				<form v-if="view === 'login-link'" @submit.prevent="login_link" class="animate__animated animate__faster animate__zoomIn">
					<div class="form-group">
						<label for="login_username">{{ frappe._('Email Address') }}</label>
						<input type="email" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.login_username}" id="login_username" v-model="login_username" :placeholder="minierp.login_name_placeholder" autofocus autocomplete="username">
						<div class="invalid-feedback mt-2" v-if="errors.login_username">{{ errors.login_username }}</div>
					</div>
					<div class="form-group">
						<button type="submit" class="btn btn-primary btn-block waves-effect waves-light" :disabled="loading" :class="{ 'loading': loading }">
							<span v-if="!loading">{{ frappe._('Send login link') }}</span>
						</button>
					</div>
					<div class="form-group">
						<button type="button" @click="change_view('login')" :disabled="loading" class="btn btn-link text-primary btn-block waves-effect waves-gray box-shadow-none border-none">{{ frappe._('Back to Login') }}</button>
					</div>
				</form>
				<form v-if="view==='signup' && !minierp.disable_signup" @submit.prevent="signup" class="animate__animated animate__faster animate__zoomIn">
					<div class="form-group">
						<label for="signup_fullname">{{ frappe._('Full Name') }}</label>
						<input type="text" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.signup_fullname}" id="signup_fullname" v-model="signup_fullname" :placeholder="frappe._('Abdo Hamoud')" autofocus autocomplete="name">
						<div class="invalid-feedback mt-2" v-if="errors.signup_fullname">{{ errors.signup_fullname }}</div>
					</div>
					<div class="form-group">
						<label for="signup_email">{{ frappe._('Email') }}</label>
						<input type="email" class="form-control form-control-lg animate__animated animate__fast" :class="{'is-invalid animate__shakeX': errors.signup_email}" id="signup_email" v-model="signup_email" :placeholder="minierp.login_name_placeholder" autocomplete="username">
						<div class="invalid-feedback mt-2" v-if="errors.signup_email">{{ errors.signup_email }}</div>
					</div>
					<div class="form-group" v-if="!minierp.disable_user_pass_login">
						<button type="submit" class="btn btn-primary btn-block waves-effect waves-light" :disabled="loading" :class="{ 'loading': loading }">
							<span v-if="!loading">{{ frappe._('Sign up') }}</span>
						</button>
					</div>
					<div class="text-center sign-up-message">
						{{ frappe._("Have an account?") }}
						<a @click="change_view('login')" class="cursor-pointer text-primary" :class="{'cursor-not-allowed': loading}" style="font-weight: 600">{{ frappe._("Login") }}</a>
					</div>
				</form>
			</div>
		</div>
		<div class="login-footer"></div>
	</div>
</template>

<script>

export default {
	name: "Login",
	setup() {
		return {
			frappe,
			minierp
		}
	},
	data() {
		return {
			view: 'login',
			login_username: null,
			login_password: null,
			forgot_username: null,
			signup_fullname: null,
			signup_email: null,
			show_password: false,
			loading: false,
			errors: {
				login_username: null,
				login_password: null,
				forgot_username: null,
				signup_fullname: null,
				signup_email: null
			},
			error_message: null,
			success_message: null
		}
	},
	computed: {
		logo() {
			return minierp?.theme_settings?.logo || '/assets/minierp_theme/images/logo-lg.png';
		},
	},
	methods: {
		login() {
			if (!this.login_username || !this.login_username.trim()) {
				this.errors.login_username = frappe._('Please enter your username');
				this.removeFieldAnimation();
				return false;
			} else if (!this.login_password) {
				this.errors.login_username = null;
				this.errors.login_password = frappe._('Please enter your password');
				this.removeFieldAnimation();
				return false;
			} else {
				this.loading = true;
				this.error_message = null;
				this.success_message = null;
				this.errors.login_username = null;
				this.errors.login_password = null;
				this.call({
					cmd: 'login',
					usr: this.login_username,
					pwd: this.login_password,
				}, null, "/login").then((response) => {
					this.error_message = null;
					this.forgot_username = null;
					window.location.href = '/app';
					// window.location.href = frappe.utils.sanitise_redirect(frappe.utils.get_url_arg("redirect-to")) || response.home_page;
				}).catch((error) => {
					this.loading = false;
					if (error.status === 401) {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._("Invalid Login. Try again.");
					} else {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._(error?.responseJSON?.exception?.replace("frappe.exceptions.SecurityException: ", ""));
					}
				});
			}
		},
		signup() {
			if (!this.signup_fullname || !this.signup_fullname.trim()) {
				this.errors.signup_fullname = frappe._('Please enter your fullname');
				this.removeFieldAnimation();
				return false;
			} else if (!this.signup_email || !this.signup_email.trim()) {
				this.errors.signup_fullname = null;
				this.errors.signup_email = frappe._('Please enter your email address');
				this.removeFieldAnimation();
				return false;
			} else if (!this._isValidEmail(this.signup_email)) {
				this.errors.signup_email = frappe._('Please enter a valid email address');
				this.removeFieldAnimation();
				return false;
			} else {
				this.loading = true;
				this.error_message = null;
				this.success_message = null;
				this.errors.signup_fullname = null;
				this.errors.signup_email = null;
				this.call({
					cmd: 'frappe.core.doctype.user.user.sign_up',
					full_name: this.signup_fullname,
					email: this.signup_email,
					redirect_to: frappe.utils.sanitise_redirect(frappe.utils.get_url_arg("redirect-to"))
				}).then(({message}) => {
					this.loading = false;
					this.error_message = null;
					this.signup_fullname = null;
					this.signup_email = null;
					this[cint(message[0]) === 1 ? 'success_message' : 'error_message'] = frappe._(message[1]);
				}).catch((error) => {
					this.success_message = null;
					this.loading = false;
					if (error.status === 401) {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._("Invalid Login. Try again.");
					} else {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._(error?.responseJSON?.exception?.replace("frappe.exceptions.SecurityException: ", ""));
					}
				});
			}
		},
		login_link() {
			if (!this.login_username || !this.login_username.trim()) {
				this.errors.login_username = frappe._('Please enter your email address');
				this.removeFieldAnimation();
				return false;
			} else if (!this._isValidEmail(this.login_username)) {
				this.errors.login_username = frappe._('Please enter a valid email address');
				this.removeFieldAnimation();
				return false;
			} else {
				this.loading = true;
				this.error_message = null;
				this.success_message = null;
				this.errors.login_username = null;
				this.call({
					cmd: 'frappe.www.login.send_login_link',
					email: (this.login_username || '').trim(),
				}).then((response) => {
					this.error_message = null;
					this.loading = false;
					this.login_username = null;
					this.success_message = frappe._("Login link has been sent to your email.");
					console.log('send_login_link', response);
				}).catch((error) => {
					console.log('send_login_link', error);
					this.loading = false;
					this.success_message = null;
					if (error.status === 404) {
						this.error_message = frappe._("Your email address is not registered.");
					} else {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._(error?.responseJSON?.exception?.replace("frappe.exceptions.SecurityException: ", "").replace("frappe.exceptions.RateLimitExceededError: ", ""));
					}
				});
			}
		},
		forgot_password() {
			if (!this.forgot_username || !this.forgot_username.trim()) {
				this.errors.forgot_username = frappe._('Please enter your email address');
				this.removeFieldAnimation();
				return false;
			} else if (!this._isValidEmail(this.forgot_username)) {
				this.errors.forgot_username = frappe._('Please enter a valid email address');
				this.removeFieldAnimation();
				return false;
			} else {
				this.loading = true;
				this.error_message = null;
				this.success_message = null;
				this.errors.forgot_username = null;
				this.call({
					cmd: 'frappe.core.doctype.user.user.reset_password',
					user: (this.forgot_username || '').trim(),
				}).then((response) => {
					this.error_message = null;
					this.loading = false;
					this.forgot_username = null;
					this.success_message = frappe._("Password reset instructions have been sent.");
				}).catch((error) => {
					this.loading = false;
					this.success_message = null;
					if (error.status === 404) {
						this.error_message = frappe._("User with email address does not exist.");
					} else {
						this.error_message = frappe._(error?.responseJSON?.message) || frappe._(error?.responseJSON?.exception?.replace("frappe.exceptions.SecurityException: ", "").replace("frappe.exceptions.RateLimitExceededError: ", ""));
					}
				});
			}
		},
		login_with_provider(provider) {
			window.location.href = provider.auth_url;
		},
		change_view(view) {
			if (this.loading) return;
			this.view = view;
			this.login_username = null;
			this.login_password = null;
			this.forgot_username = null;
			this.signup_fullname = null;
			this.signup_email = null;
			this.success_message = null;
			this.error_message = null;
			this.errors = {
				login_username: null,
				login_password: null,
				forgot_username: null,
				signup_fullname: null,
				signup_email: null,
			};
			this.show_password = false;
		},
		call(args, callback, url = "/") {
			return frappe.call({
				type: "POST",
				url: url,
				args: args,
				callback: callback,
				freeze: true,
			})
		},
		_text(text) {
			return atob(text);
		},
		removeFieldAnimation() {
			const field = $('.form-control');
			if (!field.filter('.is-invalid').hasClass('animate__shakeX'))
				field.filter('.is-invalid').addClass('animate__shakeX');
			setTimeout(() => field.removeClass('animate__shakeX'), 1500);
		},
		_isValidEmail(email) {
			return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email);
		}
	},
	mounted() {

	}
}
</script>

<style scoped>

</style>
